/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { ReactEditor } from 'slate-react';
import {
  Editor,
  Element as SlateElement,
  Path,
  Node,
  Transforms,
  Range
} from 'slate';
import shortUUID from 'short-uuid';
import { GET_WORKER_MAP } from '@/graphql/cache/playground';
import { invert } from 'lodash';
import { ApolloClient } from '@apollo/client';
import {
  EditorBlock,
  ExecutionStatus,
  ISentence,
  Sentence
} from '../Editor/editorInterface';
import { getSentenceTabCount } from '../Editor/editorUtil';

export const getNodePath = (editor: ReactEditor, node: any) => {
  // [TODO] - some when lost focus bug
  const path = ReactEditor.findPath(editor, node);
  const nodePath = path.length === 1 ? [path[0], 0] : path;
  return nodePath;
};

export const getNodeByPath = (
  editor: Editor,
  path?: Path,
  mode: 'all' | 'highest' | 'lowest' = 'lowest'
) => {
  const nodeEntry = Array.from(
    Editor.nodes(editor, {
      match: (node) => Editor.isEditor(editor) && SlateElement.isElement(node),
      at: path || editor.selection?.anchor.path,
      mode
    })
  )[0];

  if (nodeEntry) return nodeEntry[0];

  return editor.children[0];
};

// Recursively calculates the Greatest Common Divisor (GCD) of two numbers.
function gcd(a: number, b: number): number {
  if (b === 0) {
    return a;
  }
  return gcd(b, a % b);
}

export const getDefaultNewLine = () => {
  // Requires any here because of type mismatch default and custom element
  // const path: any = getNodeByPath(editor);
  const lineId = shortUUID.generate();
  return {
    type: 'EditorLine',
    children: [
      {
        uiLineId: lineId,
        text: '',
        status: ExecutionStatus.NEW_LINE,
        tabCount: 0
      }
    ]
  };
};

export const getNodeAndPath = (editor: Editor, id: string) => {
  const editorNodes = Array.from(Node.elements(editor)).map(
    (n) => n[0].children[0]
  );

  // @ts-ignore
  const index = editorNodes.findIndex((node) => node.uiLineId === id);

  return {
    path: [index, 0],
    // @ts-ignore
    node: editor.children[index].children[0]
  };
};

interface ParseTextOutput {
  documentLines: EditorBlock[];
  errorMesaage: string;
}

// Defines a function to find the common space per tab value among the given lines,
// with an early return optimization for efficiency.
export function findCommonSpacePerTab(lines: string[]): number {
  // Define the target indentation (number of spaces per indent level).
  const defaultIdentation = 2;
  // Initialize an array to store the counts of spaces at the beginning of each line that meet the target indentation.
  const spaceCounts: number[] = [];

  // Iterate through each line in the input array.
  for (const line of lines) {
    // Initialize a counter for spaces at the beginning of the line.
    let spaceCount = 0;
    // Initialize an index variable to iterate through characters in the line.
    let i = 0;

    // Loop through the beginning of the line until a non-space character is encountered.
    while (i < line.length && line[i] === ' ') {
      // Increment the space count for each space found.
      spaceCount += 1;
      // Move to the next character in the line.
      i += 1;
    }

    // If the space count for a line matches the target indentation, return it immediately.
    if (spaceCount === defaultIdentation) {
      return defaultIdentation;
    }

    // If the space count is a multiple of the target indentation and greater than 0, add it to the array.
    if (spaceCount % defaultIdentation === 0 && spaceCount > 0) {
      spaceCounts.push(spaceCount);
    }
  }

  // If no lines with the target indentation pattern are found, or if all lines exactly match the targetIndentation,
  // return the targetIndentation.
  if (spaceCounts.length === 0) {
    return defaultIdentation;
  }

  // Calculate the GCD among space counts to find the common indentation.
  let commonSpacePerTab = spaceCounts[0];
  for (let i = 1; i < spaceCounts.length; i += 1) {
    commonSpacePerTab = gcd(commonSpacePerTab, spaceCounts[i]);
  }

  // Return the common space per tab value, which is the GCD of space counts.
  return commonSpacePerTab;
}

const countTabAtBeginning = (
  inputString: string,
  spacesPerTab: number
): number => {
  let spaceCount = 0;
  let index = 0;

  while (index < inputString.length && inputString[index] === ' ') {
    spaceCount += 1;
    index += 1;
  }

  // Calculate the equivalent tab count based on the number of spaces
  const tabCount = Math.floor(spaceCount / spacesPerTab);

  return tabCount;
};

export const parseText = (text: string): ParseTextOutput => {
  const trimmedText = text.trim();
  const lines = trimmedText.split('\n');
  const outputLines: EditorBlock[] = [];
  const errorMesaage: string = '';

  // TODO: SpacePerTab should be configurable
  const spacesPerTab = findCommonSpacePerTab(lines);

  // try {
  lines.forEach((line) => {
    // Count the number of tab at the beginning of the line
    const tabCount = countTabAtBeginning(line, spacesPerTab);

    // Create a new EditorBlock
    const newBlock: EditorBlock = {
      type: 'EditorLine',
      children: [
        {
          uiLineId: shortUUID.generate(),
          text: line.trim(),
          status: ExecutionStatus.NEW_LINE,
          tabCount
        }
      ]
    };

    outputLines.push(newBlock);
  });

  return {
    documentLines: outputLines,
    errorMesaage
  };
};

/**
 * Generate text from given EditorBlock[]
 * Based on tabCount of each EditorBlock, generateText will add indentation by two spaces
 * @param blocks
 * @returns {string}
 */
export const generateText = (blocks: EditorBlock[]): string => {
  let output = '';

  blocks?.forEach((block) => {
    output += `${
      '  '.repeat(block.children[0].tabCount || 0) + block.children[0].text
    }\n`;
  });

  return output.trim();
};

/**
 * Returns the editor nodes in the current selection
 * @param editor
 * @returns {EditorBlock[]}
 */
export const getEditorNodesFromSelection = (editor: Editor): EditorBlock[] => {
  let items: EditorBlock[] = [];
  const { selection } = editor;

  if (selection) {
    const reversed = Path.isAfter(
      selection?.anchor.path,
      selection?.focus.path
    );

    items = Array.from(
      Node.elements(editor, {
        from: selection.anchor.path,
        to: selection.focus.path,
        reverse: reversed
      })
    ).map((n) => {
      if (
        selection.anchor.path[0] ===
        selection.focus.path[0 && n[1][0] === selection.focus.path[0]]
      ) {
        const text = reversed
          ? // @ts-ignore
            n[0].children[0].text.substring(
              selection.focus.offset,
              selection.anchor.offset
            )
          : // @ts-ignore
            n[0].children[0].text.substring(
              selection.anchor.offset,
              selection.focus.offset
            );
        const children = {
          ...n[0].children[0],
          text
        };
        return {
          ...n[0],
          children: [children]
        };
      }
      if (n[1][0] === selection.focus.path[0]) {
        const text = reversed
          ? // @ts-ignore
            n[0].children[0].text.substring(selection.focus.offset)
          : // @ts-ignore
            n[0].children[0].text.substring(0, selection.focus.offset);
        const children = {
          ...n[0].children[0],
          text
        };
        return {
          ...n[0],
          children: [children]
        };
      }
      if (n[1][0] === selection.anchor.path[0]) {
        const text = reversed
          ? // @ts-ignore
            n[0].children[0].text.substring(0, selection.anchor.offset)
          : // @ts-ignore
            n[0].children[0].text.substring(selection.anchor.offset);
        const children = {
          ...n[0].children[0],
          text
        };
        return {
          ...n[0],
          children: [children]
        };
      }

      return n[0];
    }) as EditorBlock[];

    if (reversed) {
      items.reverse();
    }
  }
  return items;
};

export const getParentMap = (
  editorNodes: any[]
): Map<string, string | null> => {
  // create a mapping from child ids to parent ids using a Map
  const parentMap = new Map<string, string | null>();

  for (let i = 0; i < editorNodes.length; i += 1) {
    parentMap.set(editorNodes[i].node.uiLineId, editorNodes[i].node.uiParentId);
  }

  return parentMap;
};

export const getNodeIfPresent = (
  nodes: ISentence[],
  idToFind: string
): ISentence | null => {
  for (const node of nodes) {
    if (node.uiLineId === idToFind) {
      return node;
    }
  }
  return null;
};

const isStopWords = (text: string): boolean => {
  const stopWords = ['the', 'a', 'an', 'is', 'are', 'was', 'were', 'will'];
  return !stopWords.includes(text.toLowerCase());
};

export const getHighlightedText = (editor: Editor): string => {
  const { selection } = editor;
  let text = '';
  if (!selection) {
    return text;
  }

  if (Range.isRange(selection)) {
    text = Editor.string(editor, selection);
  }

  // check if the selected word is a stop word if yes return empty string
  if (!isStopWords(text.trim())) {
    return '';
  }

  return text.trim();
};
export const resetEditorLines = (
  editor: Editor,
  startDebugLine: ISentence | null
) => {
  const editorlines = Array.from(Node.elements(editor)).map((n: any) => {
    return { node: n[0].children[0], path: n[1] };
  });

  let lineToCheck = -1;

  // Check for line if it is paused from the editorLines
  lineToCheck = editorlines.findIndex(
    (line) => line?.node?.shouldPause === true
  );

  // If line is not found, then check for the line from the startDebugLine
  // StartDebugLine will alaways be smaller than the pause line value

  const startLineIndex = editorlines.findIndex(
    (line) => line.node.uiLineId === startDebugLine?.uiLineId
  );

  if (startLineIndex !== -1) {
    lineToCheck = Math.min(lineToCheck, startLineIndex);
  }

  // TODO: Need to handle if a line is edited, then also we should reset line below that

  if (lineToCheck !== -1) {
    Editor.withoutNormalizing(editor, () => {
      for (let i = lineToCheck; i < editorlines.length; i += 1) {
        Transforms.setNodes(
          editor,
          {
            status: ExecutionStatus.NEW_LINE,
            requests: undefined,
            answer: undefined,
            editorRequests: undefined,
            editorAnswers: undefined,
            editorConcepts: undefined,
            editorSubDocuments: undefined,
            editorMiniPlaygrounds: undefined,
            loopResultCount: undefined,
            concepts: undefined
          },
          { at: [i, 0] }
        );
      }
    });
  }
};

function removeMultipleLines(editor: Editor, paths: Path[]) {
  // Sort paths in descending order to avoid path re-indexing issues
  const sortedPaths = paths.sort((a, b) => b[0] - a[0]);

  // Remove each node by its path
  sortedPaths.forEach((path) => {
    Transforms.removeNodes(editor, {
      at: path
    });
  });
}

function getEditorNodeArray(editor: Editor): { node: ISentence; path: Path }[] {
  return Array.from(Node.elements(editor)).map((n) => {
    return { node: n[0].children[0], path: n[1] };
  });
}

export function formatFlexGrammarDocument(
  editor: Editor,
  sentences: Sentence[],
  client: ApolloClient<any>
) {
  console.log('formatFlexGrammarDocument', sentences);
  let editorNodes = getEditorNodeArray(editor);

  const sentenceIds = sentences.map((s) => s.metadata?.lineId);
  // Find the nodes that are not present in the new sentences array to remove from editor nodes
  const nodesToDelete = editorNodes
    .filter((s: any) => !sentenceIds.includes(s.node.uiLineId))
    .map((s: any) => s.path);
  removeMultipleLines(editor, nodesToDelete);

  // update the editor nodes after the node deletion
  editorNodes = getEditorNodeArray(editor);

  const localIdMap: { [key: string]: string } = {};

  Editor.withoutNormalizing(editor, () => {
    const newNodesToInsert: EditorBlock[] = [];

    let selectionLocation = null;

    for (let i = 0; i < sentences.length; i += 1) {
      const currentSentence = sentences[i];

      if (currentSentence.metadata) {
        localIdMap[currentSentence.metadata.lineId] = currentSentence.lineId;

        const existingSentence = editorNodes.find(
          (s: any) => s.node.uiLineId === currentSentence.metadata!.lineId
        );

        if (existingSentence) {
          Transforms.setNodes(
            editor,
            {
              ...existingSentence.node,
              internalMetadata: currentSentence.internalMetadata
            },
            { at: [existingSentence.path[0], 0] }
          );
        } else {
          const parentNode: any = editorNodes.find(
            (s: any) => s.node.uiLineId === currentSentence.metadata!.parentId
          );

          if (!parentNode) {
            const latestParent = newNodesToInsert.find(
              (s: any) =>
                s.children[0].uiLineId === currentSentence.metadata!.parentId
            );

            if (latestParent) {
              const newChildLine: EditorBlock = {
                type: 'EditorLine',
                children: [
                  {
                    uiLineId: currentSentence.metadata.lineId,
                    text: currentSentence.text,
                    status: ExecutionStatus.NEW_LINE,
                    tabCount: getSentenceTabCount(currentSentence, sentences),
                    language: currentSentence.language,
                    internalMetadata: currentSentence.internalMetadata
                  }
                ]
              };
              newNodesToInsert.push(newChildLine);
            }
          }

          const path = parentNode?.path;

          if (parentNode && path) {
            console.log('parentNode', parentNode);
            console.log('path', path);

            const insertConfig = {
              path: [path[0], 0],
              offset: parentNode.node.text.length
            };

            console.log('insertConfig', insertConfig);

            selectionLocation = insertConfig;

            const newLine: EditorBlock = {
              type: 'EditorLine',
              children: [
                {
                  uiLineId: currentSentence.metadata.lineId,
                  text: currentSentence.text,
                  status: ExecutionStatus.NEW_LINE,
                  tabCount: (parentNode.node.tabCount || 0) + 1,
                  language: currentSentence.language,
                  internalMetadata: currentSentence.internalMetadata
                }
              ]
            };

            newNodesToInsert.push(newLine);
          }
        }
      }
    }

    console.log('nodeToInsert', newNodesToInsert);

    if (selectionLocation) {
      Transforms.select(editor, {
        anchor: selectionLocation,
        focus: selectionLocation
      });
      Transforms.insertNodes(editor, newNodesToInsert);
    }
  });

  const workerMap = client.readQuery({
    query: GET_WORKER_MAP
  });
  const map = workerMap?.getWorkerMap || {};
  const newIdMap = { ...map.idMap, ...localIdMap };
  const newReverseIdMap = { ...map.reverseIdMap, ...invert(localIdMap) };

  client.writeQuery({
    query: GET_WORKER_MAP,
    data: {
      getWorkerMap: {
        idMap: newIdMap,
        reverseIdMap: newReverseIdMap
      }
    }
  });
}
